import styled, { css } from 'styled-components';
import { breakpoints } from 'theme';

import Turnstile from '../Turnstile';

export const StyledComposerSection = styled.section<{ $fullHeight?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  max-width: 900px;
  margin: ${({ $fullHeight }) => ($fullHeight ? '12px' : '20px')} auto 0;
  padding-right: 30px;

  @media ${breakpoints.downSmPlus} {
    margin-top: ${({ $fullHeight }) => ($fullHeight ? '8px' : '12px')};
    padding-right: 12px;
  }
`;

export const StyledComposer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  text-align: left;
  font-weight: 400;
  padding: 8px;
  border-radius: 22px;
  border: 1px solid ${({ theme }) => theme.color.porcelain};
  background: ${({ theme }) => theme.color.blackSqueeze};
  box-shadow: 0 24px 48px -28px ${({ theme }) => `${theme.color.pickledBluewood}59`};

  @media ${breakpoints.downSmPlus} {
    padding: 6px;
    border-radius: 18px;
  }
`;

export const StyledSheet = styled.div`
  --composer-pad: 28px;
  --composer-scrollbar: 10px;

  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  padding: 22px 0 16px;
  border-radius: 16px;
  background: ${({ theme }) => theme.color.white};
  box-shadow: 0 1px 3px ${({ theme }) => `${theme.color.pickledBluewood}14`};

  @media ${breakpoints.downMdPlus} {
    --composer-pad: 24px;
    padding-top: 20px;
    padding-bottom: 12px;
  }

  @media ${breakpoints.downSmPlus} {
    --composer-pad: 16px;
    padding-top: 16px;
    padding-bottom: 12px;
    border-radius: 14px;
  }
`;

export const StyledSheetInset = styled.div`
  padding: 0 var(--composer-pad);
`;

export const StyledComposerBody = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.color.botticelli} transparent;

  &::-webkit-scrollbar {
    width: var(--composer-scrollbar);
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    border: 2px solid transparent;
    border-radius: 999px;
    background: ${({ theme }) => theme.color.botticelli};
    background-clip: padding-box;
  }

  &:hover::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.color.casper};
    background-clip: padding-box;
  }
`;

export const StyledComposerScrollContent = styled.div<{ $fill?: boolean }>`
  padding: 0 calc(var(--composer-pad) - var(--composer-scrollbar)) 8px
    var(--composer-pad);

  ${({ $fill }) =>
    $fill &&
    css`
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      min-height: 100%;
    `}
`;

export const StyledComposerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
`;

export const StyledComposerTitle = styled.h2`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.pickledBluewood};
`;

export const StyledHeaderIconButton = styled.button`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: ${({ theme }) => theme.color.casper};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, opacity 0.15s ease;

  svg {
    width: 15px;
    height: 15px;
  }

  &:hover {
    background: ${({ theme }) => theme.color.blackSqueeze};
    color: ${({ theme }) => theme.color.pickledBluewood};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brightTurquoise};
    outline-offset: 2px;
  }
`;

export const StyledComposerHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;

  ${StyledHeaderIconButton} {
    margin-left: 6px;
    opacity: 0.55;
  }

  &:hover ${StyledHeaderIconButton},
  ${StyledHeaderIconButton}:focus-visible,
  ${StyledHeaderIconButton}[aria-pressed='true'] {
    opacity: 1;
  }
`;

export const StyledCopyToggle = styled.button<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px 0 6px;
  border-radius: 999px;
  border: 1px solid
    ${({ active, theme }) =>
      active ? `${theme.color.brightTurquoise}66` : theme.color.porcelain};
  background: ${({ active, theme }) =>
    active ? theme.color.aquaSqueeze : theme.color.white};
  font-size: 13px;
  font-weight: 600;
  color: ${({ active, theme }) =>
    active ? theme.color.skobeloffGreen : theme.color.baliHai};
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.color.brightTurquoise};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brightTurquoise};
    outline-offset: 2px;
  }
`;

export const StyledCopyCheck = styled.span<{ active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid
    ${({ active, theme }) =>
      active ? theme.color.brightTurquoise : theme.color.botticelli};
  background: ${({ active, theme }) =>
    active ? theme.color.brightTurquoise : 'transparent'};
  color: ${({ theme }) => theme.color.white};
  transition: background 0.15s ease, border-color 0.15s ease;

  svg {
    width: 12px;
    height: 12px;
    transform: scale(${({ active }) => (active ? 1 : 0)});
    transition: transform 0.15s ease;
  }
`;

export const StyledSubjectInput = styled.input<{ hasError?: boolean }>`
  display: block;
  width: 100%;
  margin: 18px 0 10px;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.3;
  color: ${({ theme }) => theme.color.shark};

  &::placeholder {
    color: ${({ hasError, theme }) =>
      hasError ? theme.color.bittersweet : theme.color.botticelli};
  }

  @media ${breakpoints.downMdPlus} {
    font-size: 23px;
  }

  @media ${breakpoints.downSmPlus} {
    margin-top: 14px;
    font-size: 20px;
  }
`;

export const StyledInlineError = styled.div`
  margin: -4px 0 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.color.bittersweet};
`;

export const StyledFooter = styled.div`
  position: relative;
  z-index: 2;
  flex-shrink: 0;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  column-gap: 12px;
  padding: 10px 6px 4px;
  border-radius: 0 0 16px 16px;
  background: ${({ theme }) => theme.color.blackSqueeze};

  @media ${breakpoints.downSmPlus} {
    padding: 8px 2px 2px;
  }
`;

export const StyledTurnstile = styled(Turnstile)`
  display: flex;
  flex-basis: 100%;
  justify-content: flex-end;
  max-width: 100%;
  overflow: hidden;

  &:empty {
    display: none;
  }
`;

export const StyledFooterTools = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const StyledIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: ${({ theme }) => theme.color.BlueBayoux};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.color.white};
    color: ${({ theme }) => theme.color.shark};
    box-shadow: 0 1px 3px ${({ theme }) => `${theme.color.pickledBluewood}1F`};
  }

  &:active {
    transform: scale(0.96);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brightTurquoise};
    outline-offset: 1px;
  }
`;

export const StyledHiddenFileInput = styled.input`
  display: none;
`;

export const StyledDiscardButton = styled(StyledIconButton)`
  &:hover:not(:disabled) {
    color: ${({ theme }) => theme.color.bittersweet};
  }

  &:disabled,
  &:disabled:hover {
    background: transparent;
    box-shadow: none;
    color: ${({ theme }) => theme.color.BlueBayoux};
    transform: none;
    opacity: 0.35;
    cursor: not-allowed;
  }
`;

export const StyledFooterActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const StyledSendButtonWrapper = styled.span`
  display: inline-flex;
`;

export const StyledSendButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-width: 120px;
  height: 44px;
  padding: 0 22px;
  border: 1px solid transparent;
  border-radius: 12px;
  background: ${({ theme }) => theme.color.brightTurquoise};
  font-size: 15px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.black};
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease,
    background 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  svg {
    transition: transform 0.2s ease;
  }

  &:not(:disabled):hover {
    background: ${({ theme }) => theme.color.white};
    border-color: ${({ theme }) => theme.color.brightTurquoise};
    color: ${({ theme }) => theme.color.brightTurquoise};
    transform: translateY(-1px);
    box-shadow: 0 10px 20px -10px ${({ theme }) => theme.color.brightTurquoise};

    svg {
      transform: translate(2px, -2px);
    }
  }

  &:not(:disabled):active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.brightTurquoise};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  ${({ 'aria-busy': busy }) =>
    busy &&
    css`
      &:disabled {
        opacity: 0.85;
        cursor: progress;
      }
    `}

  @media ${breakpoints.downSmPlus} {
    min-width: 104px;
    height: 42px;
    padding: 0 18px;
  }
`;

export const StyledDropOverlay = styled.div`
  position: absolute;
  inset: 8px;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 16px;
  border: 2px dashed ${({ theme }) => theme.color.brightTurquoise};
  background: ${({ theme }) => `${theme.color.aquaSqueeze}EB`};
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.skobeloffGreen};
  pointer-events: none;
`;
